import { useContext, ChangeEvent, useState, useMemo } from "react";
import {
  useReactTable,
  getPaginationRowModel,
  getCoreRowModel,
  getFilteredRowModel,
} from "@tanstack/react-table";
import { TransactionSellerContext } from "../../../../../../app/contexts/TransactionSellerContext";
import { ColumnDefinitionSellerTransaction } from "../ColumnDefinitionSellerTransaction";
import { Transaction } from "@/app/entities/Transaction";
import { useAtom } from "jotai";
import { selectedIdfromTableRowTransaction } from "@/app/store";
import { useQuery } from "@tanstack/react-query";
import { configService } from "@/app/services/configService";
import { ProductCatalogItem } from "@/app/services/configService/getProductsCatalog";

export const useSellerTransactionTableController = () => {
  const { transaction, setSkip, setSearchValue } = useContext(
    TransactionSellerContext,
  );
  const { data, isFetching, isLoading } = useMemo(
    () => transaction || {},
    [transaction],
  );

  const [selectedItem, setSelectedItem] = useState<Transaction | null>(null);
  const [sheetOpen, setSheetOpen] = useState(false);
  const [, setSelectedId] = useAtom(selectedIdfromTableRowTransaction);

  const { data: productsCatalog } = useQuery<ProductCatalogItem[]>({
    queryKey: ["productsCatalog"],
    queryFn: configService.getProductsCatalog,
  });

  const { columnsSellerTransactions } =
    ColumnDefinitionSellerTransaction(productsCatalog);

  const filteredData = data?.transactions ? data?.transactions : [];

  const table = useReactTable({
    data: filteredData || [],
    columns: columnsSellerTransactions,
    getPaginationRowModel: getPaginationRowModel(),
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    initialState: {
      pagination: {
        pageSize: 20,
      },
    },
  });

  const handleRowClick = (item: Transaction) => {
    setSelectedItem(item);
    setSelectedId(item._id);
  };

  const handleSheetState = () => {
    setSheetOpen(!sheetOpen);
  };

  const handleSearchInputChange = (event: ChangeEvent<HTMLInputElement>) => {
    setSearchValue(event.target.value);
  };

  const getPageIndex = () => {
    if (!data) return 0;
    return Math.floor(data.pagination.skip / data.pagination.limit) + 1;
  };

  const getPageCount = () => {
    if (!data) return 0;
    return Math.ceil(data.pagination.total / data.pagination.limit);
  };

  const handleNextPage = () => {
    if (!data) return;
    const newPage = data.pagination.skip / data.pagination.limit + 1;
    if (getPageCount() === getPageIndex()) return;
    const skipValue = newPage * data.pagination.limit;
    setSkip(skipValue);
  };

  const handlePreviousPage = () => {
    if (!data) return;
    const newPage = data.pagination.skip / data.pagination.limit - 1;
    if (newPage < 0) return;
    const skipValue = newPage * data.pagination.limit;
    setSkip(skipValue);
  };
  return {
    isFetching,
    isLoading,
    table,
    handleNextPage,
    handlePreviousPage,
    handleSearchInputChange,
    getPageIndex,
    getPageCount,
    handleRowClick,
    sheetOpen,
    setSheetOpen,
    handleSheetState,
    selectedItem,
  };
};
